import { Quaternion, Vector3 } from "three";
import Ammo from "ammojs-typed";
import { CharacterConfig } from "@managers/physics";
import KinematicBody from "@objects/physics/KinematicBody";
import { MeshShapeController } from "@objects/physics/shapes";
import MovementController from "./movement";
class CharacterController {
    movementController;
    inputController;
    body;
    characterController;
    /**
     * A character controller responsible for handling an entity's movement using both the world physics and input controller.
     * @param entityConfig the entity's configuration object.
     * @param position the initial position.
     * @param rotation the initial rotation.
     * @param meshController the entity's mesh controller.
     * @param physicsWorld the physics world.
     * @param inputController the entity's input controller.
     */
    constructor(entityConfig, position, rotation, meshController, physicsWorld, inputController) {
        this.movementController = new MovementController(entityConfig.BASE_SPEED);
        this.inputController = inputController;
        this.body = new KinematicBody(CharacterConfig, position, rotation, new MeshShapeController(), meshController);
        this.characterController = this.createCharacterController(entityConfig);
        physicsWorld.addCollisionObject(this.body.getBody, CharacterConfig.COLLISION_FILTER_GROUP, CharacterConfig.COLLISION_FILTER_MASK);
        physicsWorld.addAction(this.characterController);
    }
    /**
     * Perform an update.
     */
    step() {
        const rotation = this.rotate();
        this.move(rotation);
        this.handleJumping();
    }
    /**
     * Rotate the character controller based on the input controller.
     * @return The character's current rotation.
     */
    rotate() {
        const rotationQuaternion = new Quaternion().copy(this.inputController.getRotation);
        const ammoQuaternion = new Ammo.btQuaternion(rotationQuaternion.x, rotationQuaternion.y, rotationQuaternion.z, rotationQuaternion.w);
        this.characterController.getGhostObject().getWorldTransform().setRotation(ammoQuaternion);
        return ammoQuaternion;
    }
    /**
     * Move the character controller based on the rotation and input controller.
     * @param rotation The character's current rotation.
     * @returns The character's current velocity.
     */
    move(rotation) {
        const velocity = this.movementController.move(this.inputController, rotation);
        this.characterController.setWalkDirection(velocity);
        return velocity;
    }
    /**
     * Determine whether the entity should jump.
     * @private
     */
    handleJumping() {
        if (this.inputController.getJUMPING && this.characterController.onGround())
            this.characterController.jump();
    }
    /**
     * Create a kinematic character controller.
     * @param entityConfig the entity's configuration object.
     * @returns the created kinematic character controller.
     * @private
     */
    createCharacterController(entityConfig) {
        const controller = new Ammo.btKinematicCharacterController(this.body.getBody, this.body.getShape, CharacterConfig.STEP_HEIGHT, CharacterConfig.UP_AXIS);
        controller.setUseGhostSweepTest(false);
        controller.setUpInterpolate(true);
        controller.setMaxSlope(entityConfig.MAX_SLOPE);
        controller.setJumpSpeed(entityConfig.JUMP_SPEED);
        controller.setMaxJumpHeight(entityConfig.MAX_JUMP_HEIGHT);
        return controller;
    }
    get getCharacterController() {
        return this.characterController;
    }
    get getPosition() {
        const position = this.characterController.getGhostObject().getWorldTransform().getOrigin();
        return new Vector3(position.x(), position.y(), position.z());
    }
    get getRotation() {
        const rotation = this.characterController
            .getGhostObject()
            .getWorldTransform()
            .getRotation();
        return new Quaternion(rotation.x(), rotation.y(), rotation.z(), rotation.w());
    }
    get getInputController() {
        return this.inputController;
    }
    get onGround() {
        return this.characterController.onGround();
    }
}
export { CharacterController };
